import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, Input, InlineError, Select, Skeleton } from '../../components/ui'
import { useToast } from '../../hooks/useToast'
import { useAppDispatch } from '../../store/hooks'
import { profileLoaded } from '../../features/auth/authSlice'
import { useGetProfileQuery, useUpdateProfileMutation } from '../../services/accountApi'
import { useTenant } from '../../tenant/useTenant'
import { formatMobile } from '../../utils/phone'

interface Draft {
  first_name: string
  last_name: string
  email: string
  gender: string
  date_of_birth: string
}

const EMPTY: Draft = { first_name: '', last_name: '', email: '', gender: '', date_of_birth: '' }

/** I6 — profile editing. The phone number is the login identity and is read-only. */
export function ProfilePage() {
  const { slug } = useTenant()
  const dispatch = useAppDispatch()
  const toast = useToast()

  const { data, isLoading, isError, error, refetch } = useGetProfileQuery(slug)
  const [save, { isLoading: saving, error: saveError }] = useUpdateProfileMutation()

  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [touched, setTouched] = useState(false)

  // The form is seeded from the server once; typing then owns the fields.
  useEffect(() => {
    if (!data) return
    setDraft({
      first_name: data.first_name ?? data.name ?? '',
      last_name: data.last_name ?? '',
      email: data.email ?? '',
      gender: data.gender ?? '',
      date_of_birth: data.date_of_birth ?? '',
    })
  }, [data])

  const set = (key: keyof Draft) => (value: string) => setDraft((d) => ({ ...d, [key]: value }))

  const nameError = touched && !draft.first_name.trim() ? 'Enter your first name.' : undefined

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (!draft.first_name.trim()) return

    const result = await save({
      slug,
      first_name: draft.first_name.trim(),
      last_name: draft.last_name.trim(),
      email: draft.email.trim() || null,
      gender: draft.gender || null,
      date_of_birth: draft.date_of_birth || null,
    })
    if (!('data' in result) || !result.data) return

    dispatch(profileLoaded(result.data))
    toast.success('Your changes are saved.')
  }

  if (isLoading) {
    return (
      <Card variant="filled">
        <Skeleton lines={5} />
      </Card>
    )
  }

  if (isError) return <InlineError error={error} onRetry={() => void refetch()} />

  return (
    <div className="max-w-3xl rounded-xl border border-primary/10 bg-surface p-8">
      <h2 className="mb-8 border-b border-primary/10 pb-4 font-display text-headline-md text-primary">
        Personal information
      </h2>

      <form onSubmit={submit} noValidate className="flex flex-col gap-6">
        {saveError && <InlineError error={saveError} />}

        <div className="grid gap-6 sm:grid-cols-2">
          <Input
            label="First name"
            value={draft.first_name}
            onChange={(e) => set('first_name')(e.target.value)}
            error={nameError}
            autoComplete="given-name"
            required
          />
          <Input
            label="Last name"
            value={draft.last_name}
            onChange={(e) => set('last_name')(e.target.value)}
            autoComplete="family-name"
          />
        </div>

        <Input
          label="Email"
          type="email"
          value={draft.email}
          onChange={(e) => set('email')(e.target.value)}
          autoComplete="email"
          hint="We'll send your appointment confirmations here."
        />

        <Input
          label="Phone number"
          value={formatMobile(data?.phone ?? '')}
          readOnly
          disabled
          hint="You sign in with this number. Ask the salon if you need to change it."
        />

        <div className="grid gap-6 sm:grid-cols-2">
          <Select
            label="Gender"
            value={draft.gender}
            onChange={(e) => set('gender')(e.target.value)}
          >
            <option value="">Prefer not to say</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </Select>
          <Input
            label="Date of birth"
            type="date"
            value={draft.date_of_birth}
            onChange={(e) => set('date_of_birth')(e.target.value)}
          />
        </div>

        <div className="pt-6">
          <Button type="submit" size="lg" loading={saving}>
            Save changes
          </Button>
        </div>
      </form>

      {/* Stitch closes the profile panel with a Security block; ours points at the
          existing reset flow rather than inventing an in-place password change. */}
      <section className="mt-16">
        <h2 className="mb-8 border-b border-primary/10 pb-4 font-display text-headline-md text-primary">Security</h2>
        <div className="flex flex-col justify-between gap-4 rounded-lg border border-primary/5 bg-surface-container p-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="font-body-lg text-body-lg font-bold text-on-surface">Password</h3>
            <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
              We&apos;ll text you a code to set a new one.
            </p>
          </div>
          <Link
            to={`/${slug}/reset`}
            className="inline-flex items-center gap-2 font-label-caps text-label-caps uppercase text-primary transition-colors hover:text-primary-container"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
              lock_reset
            </span>
            Reset
          </Link>
        </div>
      </section>
    </div>
  )
}
